import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { TrendInfo } from './TrendInfo';
import { ExpandMore } from '@mui/icons-material';
import { InventoryComparison } from '../models/InventoryComparison';

type Props = {
  categoryName: string;
  inventoryComparison: InventoryComparison;
};

/**
 * Sorts names by name and number suffix so that it looks natural.
 * Example:
 * Diffie-Hellman 512
 * Diffie-Hellman 768
 * Diffie-Hellman 2048
 *
 * Natural sort would put Diffie-Hellman 2048 first, but it looks weird.
 * This function takes two names, checks if they end with the number and if
 * they are the same, and then compares them by number naturally, instead of
 * standard string comparison.
 */
const compareStringWithNumberSuffix = (k1: string, k2: string) => {
  const hasNumberSuffix1 = / \d+$/.test(k1);
  const hasNumberSuffix2 = / \d+$/.test(k2);

  if (!hasNumberSuffix1 || !hasNumberSuffix2) {
    return k1 < k2 ? -1 : 1;
  }

  const k1WithoutNumberSuffix = k1.split(' ').slice(0, -1).join(' ');
  const k2WithoutNumberSuffix = k2.split(' ').slice(0, -1).join(' ');
  const k1NumberValue = parseInt(k1.split(' ').slice(-1)[0]);
  const k2NumberValue = parseInt(k2.split(' ').slice(-1)[0]);

  if (k1WithoutNumberSuffix === k2WithoutNumberSuffix) {
    return k1NumberValue - k2NumberValue;
  }

  return k1WithoutNumberSuffix < k2WithoutNumberSuffix ? -1 : 1;
};

export const InventoryComparisonTableRow = (props: Props) => {
  const { categoryName, inventoryComparison } = props;
  const [isOpen, setIsOpen] = useState(false);
  const subCategories = inventoryComparison.data[categoryName];
  const { currentTotalCount, previousTotalCount } = inventoryComparison.getCategoryTotals(categoryName);
  const percentage = Math.round((currentTotalCount / previousTotalCount - 1) * 100);
  const { t } = useTranslation();

  return (
    <>
      <TableRow
        aria-label={(isOpen ? t('a11y.comparison.collapseButton') : t('a11y.comparison.expandButton')) as string}
        role="button"
        tabIndex={0}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setIsOpen((prevState) => !prevState);
          }
        }}
        onClick={() => setIsOpen((prevState) => !prevState)}
        sx={{
          cursor: 'pointer',
          '&:hover, &:focus': {
            backgroundColor: 'backdrops.lightGrey',
          },
          '&:focus': {
            outline: 'solid 1px',
            outlineColor: 'primary.main',
          },
        }}
      >
        <TableCell>
          <Typography variant="body2">{categoryName}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{previousTotalCount}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{currentTotalCount}</Typography>
        </TableCell>
        <TableCell>
          <TrendInfo percentage={percentage} value={currentTotalCount - previousTotalCount} isPositiveTrendUp />
        </TableCell>
        <TableCell>
          <ExpandMore
            sx={{
              mt: 1,
              transform: isOpen ? 'rotate(180deg)' : '',
              transition: 'transform 150ms',
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0, m: 0, borderBottom: 'unset' }}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Table sx={{ tableLayout: 'fixed' }} role="presentation">
              <TableBody>
                {Object.keys(subCategories)
                  .sort(compareStringWithNumberSuffix)
                  .map((subCategoryKey) => {
                    const counts = subCategories[subCategoryKey];
                    const currentCount = counts.currentCount ?? 0;
                    const previousCount = counts.previousCount ?? 0;
                    const percentage = Math.round((currentCount / previousCount - 1) * 100);

                    return (
                      <TableRow key={subCategoryKey} sx={{ backgroundColor: '#0078BB14' }}>
                        <TableCell width="35%">
                          <Typography variant="body2">{subCategoryKey}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{previousCount}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{currentCount}</Typography>
                        </TableCell>
                        <TableCell>
                          <TrendInfo percentage={percentage} value={currentCount - previousCount} isPositiveTrendUp />
                        </TableCell>
                        <TableCell width="5%" align="right">
                          <Box sx={{ width: '1.5rem', h: '1px' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
